import React, { useEffect, useRef, useState } from 'react'
import { AlertProps } from 'antd/lib/alert'
import { Col, Divider, Form, notification, Row } from 'antd'
import { Formik, FormikProps } from 'formik'
import { History } from 'history'
import { RouteComponentProps } from 'react-router-dom'
import { TreeNode } from 'antd/lib/tree-select'
import { useTranslation } from 'react-i18next'

import { AssignTree, PAssignTreeType } from 'src/types'
import { Button, Card, PageHeader } from 'src/components/Ant'
import * as TreesService from 'src/services/api/trees'
import * as UsersService from 'src/services/api/users'

import { Spin } from './styles'
import * as Values from './initial-values'
import sanitize from './sanitize'
import UserAddress from './user-address'
import UserAirInfo from './user-air-info'
import UserAssignments from './user-assignments'
import UserBasic from './user-basic'
import UserBasicTitle from './user-basic-title'
import UserContacts from './user-contacts'
import UserInfo from './user-info'
import UserRequester from './user-requester'
import UserRoleForm from './user-role-form'
import UserSetPassword from './user-set-password'
import UserVehicleInfo from './user-vehicle-info'
import validationSchema from './validation-schema'

import { WrapContent, WrapAlert } from '../styles'

type Params = {
  user_id?: string
}

interface UserFormComponentsProps {
  formikProps: FormikProps<Values.InitialValues>
  hadError: AlertProps
  history: History<unknown>
  isLoading: boolean
  isSaving: boolean
}

function mapTreeRecursive(item: AssignTree): TreeNode {
  return {
    checkable:
      item.p_assign_tree_type === PAssignTreeType.COST_CENTER ||
      item.p_assign_tree_type === PAssignTreeType.ORGANIZATION,
    children: item.childrens
      .filter(treeItem => treeItem.p_assign_tree_type !== PAssignTreeType.USER)
      .map(mapTreeRecursive),
    key: `${item.assign_tree_id}`,
    title: item.assign_tree_name,
    value: `${item.assign_tree_id}`
  }
}

function UserFormComponents({
  formikProps,
  hadError,
  history,
  isLoading
}: UserFormComponentsProps) {
  const [trees, setTrees] = useState<AssignTree[]>([])
  const [loadingTrees, setLoadingTrees] = useState(false)

  useEffect(() => {
    let isSubscribed = true
    setLoadingTrees(true)
    TreesService.getAll()
      .then(response => {
        if (isSubscribed) {
          setTrees(response.data.trees)
        }
      })
      .catch(() => {
        //
      })
      .finally(() => {
        if (isSubscribed) {
          setLoadingTrees(false)
        }
      })
    return () => {
      isSubscribed = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (hadError) {
    return (
      <Row>
        <Col>
          <WrapAlert
            description={hadError.description}
            message={hadError.message}
            showIcon
            type={hadError.type || 'error'}
          />
        </Col>
      </Row>
    )
  }

  if (isLoading) {
    return (
      <Row>
        <Col>
          <Spin />
        </Col>
      </Row>
    )
  }

  return (
    <>
      <Row gutter={24}>
        <Col offset={4} span={16}>
          <UserBasicTitle />
          <UserAssignments
            formikProps={formikProps}
            loadingTrees={loadingTrees}
            mapTreeRecursive={mapTreeRecursive}
            trees={trees}
          />
          <UserSetPassword formikProps={formikProps} />
          <UserBasic formikProps={formikProps} />
          <Divider />
          <UserAddress formikProps={formikProps} />
          <Divider />
          <UserVehicleInfo formikProps={formikProps} />
          <Divider />
          <UserAirInfo formikProps={formikProps} />
          <Divider />
          <UserContacts formikProps={formikProps} />
          <Divider />
          <UserInfo formikProps={formikProps} />
          <Divider />
          <UserRequester formikProps={formikProps} />
          <UserRoleForm
            formikProps={formikProps}
            loadingTrees={loadingTrees}
            mapTreeRecursive={mapTreeRecursive}
            trees={trees}
          />
        </Col>
      </Row>
      <Row align="middle" justify="end" gutter={24} type="flex">
        <Button
          loading={isLoading}
          size="default"
          title="common.actions.cancel"
          type="default"
          onClick={() => history.goBack()}
          style={{ marginRight: 8 }}
        />
        <Button
          loading={isLoading}
          key="common.actions.save"
          onClick={() => {}}
          size="default"
          title="common.actions.save"
          type="primary"
          htmlType="submit"
        />
      </Row>
    </>
  )
}

export default function UsersForm({
  history,
  match: {
    params: { user_id }
  }
}: RouteComponentProps<Params>): JSX.Element {
  const refFormik = useRef<Formik<Values.InitialValues>>()
  const isSubscribed = useRef(true)

  const [hadError, setHadError] = useState<AlertProps | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>()
  const [isSaving, setIsSaving] = useState<boolean>()

  const { t } = useTranslation()

  useEffect(
    () => () => {
      isSubscribed.current = false
    },
    []
  )

  async function onSubmit(values: Values.InitialValues) {
    setIsSaving(true)
    setHadError(null)
    const user = sanitize(values)
    UsersService.save({ user })
      .then(response => {
        if (isSubscribed.current) {
          if (response.status === 200) {
            notification.success({
              duration: 10,
              message: t('common.edit-done-successfully'),
              placement: 'bottomRight'
            })
            history.push('/users')
            return
          }

          //@ts-ignore
          const { reasons } = response.data
          const message =
            typeof reasons === 'string'
              ? reasons
              : t('common.response.status-500')
          notification.error({
            message,
            placement: 'bottomRight',
            duration: 10
          })
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        if (isSubscribed.current) {
          setIsLoading(false)
        }
      })
  }

  useEffect(() => {
    if (user_id) {
      setIsLoading(true)
      UsersService.getById({
        user_id
      })
        .then(response => {
          if (isSubscribed.current) {
            if (response.data) {
              const login_splited = response.data.login.split('@')

              refFormik.current.resetForm({
                ...response.data,
                address: !response.data.address?.address_id
                  ? null
                  : response.data.address,
                emailContact: response.data.contacts.find(
                  contactItem => contactItem.contact_type === 'EMAIL'
                ),
                login_splited: login_splited[0],
                login_sufix: `@${login_splited[1]}`,
                phoneContact: response.data.contacts.find(
                  contactItem => contactItem.contact_type === 'PHONE'
                )
              })
              return
            }
            notification.warning({
              duration: 10,
              message: t('common.response.unexpected-error'),
              placement: 'bottomRight'
            })
          }
        })
        .catch(response => {
          notification.warning({
            duration: 10,
            message: t('common.response.unexpected-error'),
            placement: 'bottomRight'
          })
        })
        .finally(() => {
          if (isSubscribed.current) {
            setIsLoading(false)
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id])

  return (
    <Formik
      initialValues={Values.initialValues}
      onSubmit={onSubmit}
      ref={refFormik}
      render={formikProps => (
        <Form onSubmit={formikProps.handleSubmit}>
          <PageHeader
            extra={[
              <Button
                key="common.actions.cancel"
                loading={isLoading || isSaving}
                onClick={() => history.goBack()}
                size="default"
                title="common.actions.cancel"
                type="default"
              />,
              <Button
                htmlType="submit"
                key="common.actions.save"
                loading={isLoading || isSaving}
                size="default"
                title="common.actions.save"
                type="primary"
              />
            ]}
            title="web.user.title"
          />
          <WrapContent>
            <Card>
              <UserFormComponents
                history={history}
                formikProps={formikProps}
                hadError={hadError}
                isLoading={isLoading}
                isSaving={isSaving}
              />
            </Card>
          </WrapContent>
        </Form>
      )}
      validateOnChange={false}
      validationSchema={validationSchema}
    />
  )
}
